/**
 * Data normalization utilities for general use across the application.
 * These functions normalize common data types like emails, phone numbers, and names.
 */

/**
 * Normalizes an email address by converting to lowercase, trimming whitespace,
 * and removing dots from Gmail/Googlemail addresses (per Google Ads Enhanced Conversions spec).
 * @param {string} email - The email address to normalize.
 * @returns {string|null} The normalized email or null if empty.
 */
export function normalizeEmail(email) {
  if (!email || typeof email !== "string") {
    return null;
  }

  let normalized = email.trim().toLowerCase();
  const parts = normalized.split("@");

  // Remove dots from Gmail/Googlemail addresses per Google Ads spec
  if (
    parts.length === 2 &&
    (parts[1] === "gmail.com" || parts[1] === "googlemail.com")
  ) {
    parts[0] = parts[0].replace(/\./g, "");
    normalized = parts.join("@");
  }

  return normalized.length > 0 ? normalized : null;
}

/**
 * Normalizes an email address for Bing Ads enhanced conversions.
 * Bing requirements: remove whitespaces, accents, "+" aliases, all periods before "@", trailing periods, lowercase.
 * @param {string} email - The email address to normalize.
 * @returns {string|null} The normalized email or null if invalid.
 */
export function normalizeBingEmail(email) {
  if (!email || typeof email !== "string") {
    return null;
  }

  // Start with basic normalization (trim, lowercase)
  let normalized = email.trim().toLowerCase();

  // Validate contains "@" sign
  if (!normalized.includes("@")) {
    return null;
  }

  // Remove all whitespaces (including internal spaces)
  normalized = normalized.replace(/\s+/g, "");

  // Remove accents (normalize to NFD and remove diacritics)
  normalized = normalized.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

  // Split at "@" to work on local part and domain separately
  const parts = normalized.split("@");
  if (parts.length !== 2) {
    return null;
  }

  let localPart = parts[0];
  const domain = parts[1];

  // Remove everything between "+" and end of local part (+ aliases)
  const plusIndex = localPart.indexOf("+");
  if (plusIndex !== -1) {
    localPart = localPart.substring(0, plusIndex);
  }

  // Remove all periods from local part
  localPart = localPart.replace(/\./g, "");

  // Validate local part is not empty after all transformations
  if (!localPart || localPart.length === 0) {
    return null;
  }

  // Reconstruct email
  normalized = localPart + "@" + domain;

  // Remove trailing period if exists
  if (normalized.endsWith(".")) {
    normalized = normalized.slice(0, -1);
  }

  // Final validation
  if (!normalized.includes("@") || normalized.length === 0) {
    return null;
  }

  return normalized;
}

/**
 * Normalizes a phone number to E.164 format, handling UK phone numbers by default with support for international formats.
 * @param {string} phone - The phone number to normalize.
 * @param {string} defaultCountryCode - Default country code if not present (default: '44' for UK).
 * @returns {string|null} The normalized phone number in E.164 format or null if invalid.
 */
export function normalizePhoneNumber(phone, defaultCountryCode = "44") {
  if (!phone || typeof phone !== "string") {
    return null;
  }

  // Handle special UK notation +44 (0) where (0) should be removed
  let processedPhone = phone;
  if (phone.includes("+44") && phone.includes("(0)")) {
    processedPhone = phone.replace("(0)", "");
  }

  // Remove all non-numeric characters except leading +
  let cleaned = processedPhone.replace(/[^\d+]/g, "");

  // Remove any + that's not at the beginning
  if (cleaned.indexOf("+") > 0) {
    cleaned = cleaned.replace(/\+/g, "");
  }

  // Convert 00 international prefix to +
  if (cleaned.startsWith("00")) {
    cleaned = "+" + cleaned.substring(2);
  }

  // Handle special Irish notation +353 (0) where (0) should be removed
  if (processedPhone.includes("+353") && processedPhone.includes("(0)")) {
    // Re-clean after removing the (0)
    cleaned = processedPhone.replace("(0)", "").replace(/[^\d+]/g, "");
  }

  // If number starts with +, it's already in international format
  if (cleaned.startsWith("+")) {
    // Ensure it's valid length
    if (cleaned.length < 10 || cleaned.length > 15) {
      return null;
    }
    return cleaned;
  }

  // Handle country-specific formats
  if (defaultCountryCode === "44") {
    // Check if number already starts with country code without +
    if (cleaned.startsWith("44")) {
      // Add the + prefix
      if (cleaned.length >= 12 && cleaned.length <= 13) {
        return "+" + cleaned;
      }
    }

    // Remove leading 0 from UK numbers
    if (cleaned.startsWith("0")) {
      cleaned = cleaned.substring(1);
    }

    // UK mobile numbers (7xxx) should be 10 digits after country code
    // UK landline numbers (1xxx, 2xxx, 3xxx) vary in length
    if (
      cleaned.length === 10 &&
      (cleaned.startsWith("7") ||
        cleaned.startsWith("1") ||
        cleaned.startsWith("2") ||
        cleaned.startsWith("3"))
    ) {
      return "+44" + cleaned;
    }
  } else {
    // For most countries, remove leading 0 when adding country code
    // This applies to Ireland (353), France (33), Germany (49), etc.
    if (cleaned.startsWith("0")) {
      cleaned = cleaned.substring(1);
    }
  }

  // For other countries or if UK validation fails, prepend country code
  if (cleaned.length >= 7 && cleaned.length <= 15) {
    // Don't double-add country code if it might already be there
    if (cleaned.startsWith(defaultCountryCode)) {
      return "+" + cleaned;
    }
    return "+" + defaultCountryCode + cleaned;
  }

  return null;
}

/**
 * Normalizes a name by trimming whitespace and proper casing.
 * @param {string} name - The name to normalize.
 * @returns {string|null} The normalized name or null if invalid.
 */
export function normalizeName(name) {
  if (!name || typeof name !== "string") {
    return null;
  }

  const trimmed = name.trim();
  if (trimmed.length === 0) {
    return null;
  }

  // Convert to proper case (first letter uppercase, rest lowercase)
  return trimmed
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/**
 * @typedef {object} SplitNameResult
 * @property {string|null} first_name - The first name part.
 * @property {string|null} last_name - The last name part.
 */

/**
 * Splits a full name into first and last name (best effort).
 * @param {string} fullName - The full name to split.
 * @returns {SplitNameResult} Object with first_name and last_name.
 */
export function splitName(fullName) {
  if (!fullName || typeof fullName !== "string" || !fullName.trim()) {
    return { first_name: null, last_name: null };
  }

  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 1) {
    return { first_name: parts[0], last_name: null };
  }

  // First part is first name, rest is last name
  const firstName = parts[0];
  const lastName = parts.slice(1).join(" ");

  return { first_name: firstName, last_name: lastName };
}

/**
 * Normalizes a postal code by trimming whitespace and converting to uppercase.
 * @param {string} postalCode - The postal code to normalize.
 * @returns {string|null} The normalized postal code or null if invalid.
 */
export function normalizePostalCode(postalCode) {
  if (!postalCode || typeof postalCode !== "string") {
    return null;
  }

  // Trim whitespace and convert to uppercase (preserve internal spaces)
  const normalized = postalCode.trim().toUpperCase();

  return normalized.length > 0 ? normalized : null;
}

/**
 * Normalizes a country name to its ISO 3166-1 alpha-2 code for Google Ads Enhanced Conversions.
 * @param {string} countryName - The full country name to normalize.
 * @returns {string} The 2-letter country code or the original name if not found.
 */
export function normalizeCountryNameToCode(countryName) {
  if (!countryName || typeof countryName !== "string") {
    return "";
  }

  const normalized = countryName.trim().toLowerCase();

  // Map of country names to ISO codes
  const countryMap = {
    // United Kingdom variations
    "united kingdom": "GB",
    uk: "GB",
    "great britain": "GB",
    britain: "GB",
    england: "GB",
    scotland: "GB",
    wales: "GB",
    "northern ireland": "GB",

    // United States variations
    "united states": "US",
    "united states of america": "US",
    usa: "US",
    us: "US",
    america: "US",

    // Western Europe
    ireland: "IE",
    "republic of ireland": "IE",
    france: "FR",
    germany: "DE",
    spain: "ES",
    portugal: "PT",
    italy: "IT",
    netherlands: "NL",
    holland: "NL",
    belgium: "BE",
    luxembourg: "LU",
    switzerland: "CH",
    austria: "AT",
    denmark: "DK",
    sweden: "SE",
    norway: "NO",
    finland: "FI",

    // Other major countries
    canada: "CA",
    australia: "AU",
    "new zealand": "NZ",
    japan: "JP",
    china: "CN",
    india: "IN",
    brazil: "BR",
    mexico: "MX",
    "south africa": "ZA",
    russia: "RU",
    poland: "PL",
    greece: "GR",
    "czech republic": "CZ",
    czechia: "CZ",
    hungary: "HU",
    romania: "RO",
    bulgaria: "BG",
    croatia: "HR",
    slovakia: "SK",
    slovenia: "SI",
    estonia: "EE",
    latvia: "LV",
    lithuania: "LT",
  };

  // Check if it's already a 2-letter ISO code
  if (countryName.length === 2) {
    const upperCode = countryName.toUpperCase();
    // Special case: UK should map to GB (UK is reserved but not the official ISO code)
    if (upperCode === "UK") {
      return "GB";
    }
    return upperCode;
  }

  // Return the code if found, otherwise return the original name
  return countryMap[normalized] || countryName;
}

/**
 * Creates an address object from individual parameters.
 * @param {string} firstName - First name.
 * @param {string} lastName - Last name.
 * @param {string} street - Street address (optional, may be excluded for privacy).
 * @param {string} city - City.
 * @param {string} region - Region/province/state.
 * @param {string} postalCode - Postal code.
 * @param {string} country - Country name or code.
 * @returns {object} The address object (not normalized).
 */
export function createAddressObject(
  firstName,
  lastName,
  street,
  city,
  region,
  postalCode,
  country,
) {
  return {
    first_name: firstName,
    last_name: lastName,
    street: street,
    city: city,
    region: region,
    postal_code: postalCode,
    country: country,
  };
}

/**
 * Normalizes an address object by applying proper formatting to all fields.
 * Preserves original field names (province/region, postcode/postal_code).
 * Does NOT convert country names to country codes - keeps country as trimmed string.
 * @param {object} addressData - The address data object.
 * @returns {object|null} The normalized address object or null if no valid data.
 */
export function normalizeAddress(addressData) {
  if (!addressData || typeof addressData !== "object") {
    return null;
  }

  const normalized = {};

  // Normalize first and last name
  if (addressData.first_name) {
    const firstName = normalizeName(addressData.first_name);
    if (firstName) normalized.first_name = firstName;
  }

  if (addressData.last_name) {
    const lastName = normalizeName(addressData.last_name);
    if (lastName) normalized.last_name = lastName;
  }

  // Normalize city name
  if (addressData.city) {
    const city = normalizeName(addressData.city);
    if (city) normalized.city = city;
  }

  // Handle province (if present)
  if (addressData.province) {
    const province = addressData.province.trim().toUpperCase();
    if (province) normalized.province = province;
  }

  // Handle region (if present)
  if (addressData.region) {
    const region = addressData.region.trim().toUpperCase();
    if (region) normalized.region = region;
  }

  // Handle postcode (if present)
  if (addressData.postcode) {
    const postcode = normalizePostalCode(addressData.postcode);
    if (postcode) normalized.postcode = postcode;
  }

  // Handle postal_code (if present)
  if (addressData.postal_code) {
    const postalCode = normalizePostalCode(addressData.postal_code);
    if (postalCode) normalized.postal_code = postalCode;
  }

  // Keep country as trimmed string (do not convert to code)
  if (addressData.country) {
    const country = addressData.country.trim();
    if (country) normalized.country = country;
  }

  // Return null if no valid address data
  return Object.keys(normalized).length > 0 ? normalized : null;
}
